import { useState } from 'react'
import { useSnapshot } from 'valtio'
import { noCase } from 'change-case'
import { miscUiState, openOptionsMenu } from './globalState'
import { AppOptions, options, resetOptions } from './optionsStorage'
import Button from './react/Button'
import { OptionMeta, OptionSlider } from './react/OptionsItems'
import Slider from './react/Slider'
import { getScreenRefreshRate } from './utils'
import { packetsReplayState } from './react/state/packetsReplayState'

export const guiOptionsScheme: {
  [t in OptionsGroupType]: Array<{ [K in keyof AppOptions]?: Partial<OptionMeta<AppOptions[K]>> } & { custom? }>
} = {
  render: [
    {
      custom () {
        return <Category>Render</Category>
      },
    },
    {
      custom () {
        const frameLimitValue = useSnapshot(options).frameLimit
        const [frameLimitMax, setFrameLimitMax] = useState(null as number | null)

        return <div className='options-frame-limit-row' style={{ display: 'flex', width: '100%', gap: 3, alignItems: 'stretch' }}>
          <Slider
            className='options-frame-limit-slider'
            style={{ flex: 1, width: '100%', minWidth: 0 }}
            label='Frame Limit'
            disabledReason={frameLimitMax ? undefined : 'press lock button first'}
            unit={frameLimitValue ? 'fps' : ''}
            valueDisplay={frameLimitValue || 'VSync'}
            value={frameLimitValue || frameLimitMax! + 1} min={20}
            max={frameLimitMax! + 1} updateValue={(newVal) => {
              options.frameLimit = newVal > frameLimitMax! ? false : newVal
            }}
          />
          <Button
            className='options-frame-limit-unlock'
            style={{ width: 21, flexShrink: 0 }} icon='pixelarticons:lock-open' onClick={async () => {
              const rate = await getScreenRefreshRate()
              setFrameLimitMax(rate)
            }}
          />
        </div>
      }
    },
    {
      backgroundRendering: {
        text: 'Background FPS limit',
      },
    },
    {
      dayCycleAndLighting: {
        text: 'Day Cycle & Lighting',
        tooltip: 'Включает смену дня/ночи и освещение. Выключите, чтобы зафиксировать освещение (полезно для реплеев).',
      },
    },
    {
      smoothLighting: {
        text: 'Smooth Lighting',
        tooltip: 'Сглаживание освещения: лучше картинка, но может быть чуть тяжелее для GPU/CPU.',
      },
    },
    {
      newVersionsLighting: {
        text: 'New Versions Lighting',
        tooltip: 'Использовать более современную модель освещения (может отличаться от старых версий Minecraft).',
      },
    },
    {
      disableBlockEntityTextures: {
        text: 'Disable Block Entity Textures',
        tooltip: 'Отключить текстуры блок-сущностей (сундуки, таблички и т.д.). Включите (ON), если нужна производительность.',
      },
    },
    {
      renderEntities: {
        text: 'Render Entities',
        tooltip: 'Отображать сущности (мобы, предметы, игроки).',
      },
    },
    {
      loadPlayerSkins: {
        text: 'Load Player Skins',
        tooltip: 'Загружать скины игроков. Выключите, если нужно быстрее и без внешних запросов.',
      },
    },
    {
      renderEars: {
        text: 'Render Ears',
        tooltip: 'Показывать косметику Ears (если поддерживается).',
      },
    },
    {
      showChunkBorders: {
        text: 'Show Chunk Borders',
        tooltip: 'Показывать границы чанков (для отладки/съёмки).',
      },
    },
    {
      highlightBlockColor: {
        text: 'Highlight Block Color',
      },
    },
  ],
  main: [
    {
      custom () {
        return <Button className='options-nav-entry' label='HUD' onClick={() => openOptionsMenu('hud')} inScreen />
      },
    },
    {
      custom () {
        return <Button className='options-nav-entry' label='Interface' onClick={() => openOptionsMenu('interface')} inScreen />
      },
    },
    {
      custom () {
        return <Button className='options-nav-entry' label='Render' onClick={() => openOptionsMenu('render')} inScreen />
      },
    },
    {
      custom () {
        return <Button className='options-nav-entry' label='Sound' onClick={() => openOptionsMenu('sound')} inScreen />
      },
    },
    {
      custom () {
        return <Button className='options-nav-entry' label='Advanced' onClick={() => openOptionsMenu('advanced')} inScreen />
      },
    },
  ],
  replay: [
    {
      custom () {
        return null
      },
    },
    {
      custom () {
        const { replayMinecraftVersion } = useSnapshot(packetsReplayState)
        const versionText = replayMinecraftVersion ? `Minecraft ${replayMinecraftVersion}` : 'Minecraft ?'
        return <Button
          inScreen
          label={versionText}
          disabled
        />
      },
    },
    {
      custom () {
        return <Button label='Settings' onClick={() => openOptionsMenu('main')} inScreen />
      },
    },
    {
      fov: {
        min: 30,
        max: 110,
        unit: '',
      }
    },
    {
      guiScale: {
        max: 4,
        min: 1,
        unit: '',
        delayApply: true,
      },
    },
    {
      custom () {
        const sp = miscUiState.singleplayer || !miscUiState.gameLoaded
        const id = sp ? 'renderDistance' : 'multiplayerRenderDistance'
        return <OptionSlider item={{
          type: 'slider',
          id,
          text: 'Render Distance',
          unit: '',
          max: sp ? 16 : 12,
          min: 1
        }}
        />
      },
    },
  ],
  hud: [
    {
      custom () {
        return <Category>HUD</Category>
      },
    },
    {
      custom () {
        return <UiToggleButton name='title' addUiText />
      },
    },
    {
      custom () {
        return <UiToggleButton name='chat' addUiText />
      },
    },
    {
      custom () {
        return <UiToggleButton name='scoreboard' addUiText />
      },
    },
    {
      custom () {
        return <UiToggleButton name='effects' label='Effects' />
      },
    },
    {
      custom () {
        return <UiToggleButton name='indicators' label='Game Indicators' />
      },
    },
    {
      custom () {
        return <UiToggleButton name='hotbar' />
      },
    },
  ],
  interface: [
    {
      chatWidth: {
        text: 'Chat Width',
        unit: 'px',
        min: 160,
        max: 640,
      },
    },
    {
      chatHeight: {
        text: 'Chat Height',
        unit: 'px',
        min: 80,
        max: 360,
      },
    },
    {
      chatScale: {
        text: 'Chat Scale',
        unit: '%',
        min: 10,
        max: 200,
      },
    },
    {
      chatOpacity: {
        text: 'Chat Opacity',
        unit: '%',
        min: 0,
        max: 100,
      },
    },
    {
      chatOpacityOpened: {
        text: 'Chat Opacity (Opened)',
        unit: '%',
        min: 0,
        max: 100,
      },
    },
    {
      messagesLimit: {
        text: 'Messages Limit',
        min: 50,
        max: 1000,
        unit: '',
      },
    },
    {
      topRightTimeDisplay: {
        text: 'Time Display',
        tooltip: 'Показывать время в правом верхнем углу: всегда / только в полноэкранном / никогда.',
      },
    },
    {
      showHand: {
        text: 'Show Hand',
      },
    },
    {
      viewBobbing: {
        text: 'View Bobbing',
        tooltip: 'Лёгкое покачивание камеры при ходьбе (визуальный эффект). На реплей не влияет, только отображение.',
      },
    },
  ],
  sound: [
    { volume: {} },
    {
      enableMusic: {
        text: 'Music',
      },
    },
    {
      custom () {
        return <OptionSlider
          valueOverride={options.enableMusic ? undefined : 0}
          onChange={(value) => {
            options.musicVolume = value
          }}
          item={{
            type: 'slider',
            id: 'musicVolume',
            text: 'Music Volume',
            min: 0,
            max: 100,
            unit: '%',
          }}
        />
      },
    },
    // { ignoreSilentSwitch: {} },
  ],
  advanced: [
    {
      custom () {
        return <Button
          inScreen
          onClick={() => {
            if (confirm('Are you sure you want to reset all settings?')) resetOptions()
          }}
        >Reset settings</Button>
      },
    },
  ],
}
export type OptionsGroupType = 'main' | 'replay' | 'hud' | 'interface' | 'render' | 'sound' | 'advanced'

const Category = ({ children }) => <div style={{
  fontSize: 9,
  textAlign: 'center',
  gridColumn: 'span 2'
}}>{children}</div>

const UiToggleButton = ({ name, addUiText = false, label = noCase(name) }: { name: string, addUiText?: boolean, label?: string }) => {
  const { disabledUiParts } = useSnapshot(options)

  const currentlyDisabled = disabledUiParts.includes(name)
  if (addUiText) label = `${label} UI`
  const enabled = !currentlyDisabled

  return <Button
    inScreen
    className={`options-toggle-button ${enabled ? 'options-toggle-on' : 'options-toggle-off'}`}
    postLabel={<span className={`options-value-badge ${enabled ? 'options-value-badge-on' : 'options-value-badge-off'}`}>{enabled ? 'ON' : 'OFF'}</span>}
    onClick={() => {
      const newDisabledUiParts = currentlyDisabled ? disabledUiParts.filter(x => x !== name) : [...disabledUiParts, name]
      options.disabledUiParts = newDisabledUiParts
    }}
  >{label}</Button>
}

export const tryFindOptionConfig = (option: keyof AppOptions) => {
  for (const group of Object.values(guiOptionsScheme)) {
    for (const optionConfig of group) {
      if (option in optionConfig) {
        return optionConfig[option]
      }
    }
  }

  return null
}
